import firebase from "firebase/compat/app";
import "firebase/compat/functions";

const getFunctions = () => firebase.app().functions("us-central1");

/**
 * Promote (approve) an ASI prompt candidate using the same TCI promotion callable.
 * @param {{ candidateId: string }} payload
 */
export async function promotePromptCandidate(payload) {
  const callable = getFunctions().httpsCallable("approvePromptCandidate");
  const result = await callable(payload);
  return result.data;
}

/**
 * Reject an ASI prompt candidate using the same TCI rejection callable.
 * @param {{ candidateId: string, reviewNotes?: string }} payload
 */
export async function rejectPromptCandidate(payload) {
  const callable = getFunctions().httpsCallable("rejectPromptCandidate");
  const result = await callable(payload);
  return result.data;
}

/**
 * List pending candidates for the current Interaction Manager (scope-filtered server-side).
 * @param {{ institutionId?: string }} payload
 */
export async function listPendingCandidates(payload = {}) {
  const callable = getFunctions().httpsCallable("listAsiPendingCandidates");
  const result = await callable(payload);
  return result.data;
}

/**
 * Approve go-live for a single ASI workflow node (design → live).
 * Callable: approveAsiInteractionGoLive
 * @param {{ workflowId: string, nodeId: string, interactionId?: string }} payload
 * @returns {Promise<{ workflowId: string, nodeId: string, status: "live", workflowStatus: string }>}
 */
export async function approveInteractionGoLive(payload) {
  const callable = getFunctions().httpsCallable("approveAsiInteractionGoLive");
  const result = await callable(payload);
  return result.data;
}

/**
 * Clone a library template into the caller's institution.
 * Callable: cloneAsiWorkflowFromTemplate (server requires Interaction Manager canApprove).
 * @param {{ templateWorkflowId: string, institutionId: string, newWorkflowId?: string }} payload
 * @returns {Promise<{ workflowId: string }>}
 */
export async function cloneWorkflowFromTemplate(payload) {
  const callable = getFunctions().httpsCallable("cloneAsiWorkflowFromTemplate");
  const result = await callable(payload);
  return result.data;
}

/**
 * Approve (promote) a pending ASI structural graph candidate.
 * Callable: approveAsiGraphCandidate (workflow owner only).
 * @param {{ candidateId: string }} payload
 * @returns {Promise<{ candidateId: string, workflowId: string, status: string, workflowStatus: string }>}
 */
export async function approveGraphCandidate(payload) {
  const callable = getFunctions().httpsCallable("approveAsiGraphCandidate");
  const result = await callable(payload);
  return result.data;
}

/**
 * Reject a pending ASI structural graph candidate.
 * Callable: rejectAsiGraphCandidate (workflow owner only).
 * @param {{ candidateId: string, reviewNotes?: string }} payload
 * @returns {Promise<{ candidateId: string, workflowId: string, status: string }>}
 */
export async function rejectGraphCandidate(payload) {
  const callable = getFunctions().httpsCallable("rejectAsiGraphCandidate");
  const result = await callable(payload);
  return result.data;
}

/**
 * Retag an ASI Interaction's businessUnitId (hierarchy node id, or null to clear).
 * Callable: setAsiInteractionBusinessUnit (IM canApprove on Interaction, or platform admin).
 * @param {{ interactionId: string, businessUnitId: string|null }} payload
 */
export async function setInteractionBusinessUnit(payload) {
  const callable = getFunctions().httpsCallable("setAsiInteractionBusinessUnit");
  const result = await callable(payload);
  return result.data;
}

/**
 * Upsert thin asiBusinessUnits policy (attributionMode, optional displayName).
 * Callable: upsertAsiBusinessUnit (org-scoped IM or platform admin).
 * @param {{
 *   businessUnitId: string,
 *   institutionId: string,
 *   attributionMode?: "named"|"anonymized",
 *   displayName?: string|null,
 * }} payload
 */
export async function upsertBusinessUnit(payload) {
  const callable = getFunctions().httpsCallable("upsertAsiBusinessUnit");
  const result = await callable(payload);
  return result.data;
}

/**
 * Upsert an ASI role assignment (Trainer / Interaction Manager at a scope).
 * Callable: upsertAsiRoleAssignment
 * @param {{
 *   uid: string,
 *   role: "trainer"|"interactionManager"|"asiReadinessLead",
 *   scopeType: "interaction"|"businessUnit"|"org",
 *   scopeId: string,
 *   institutionId: string,
 * }} payload
 */
export async function upsertAsiRoleAssignment(payload) {
  const callable = getFunctions().httpsCallable("upsertAsiRoleAssignment");
  const result = await callable(payload);
  return result.data;
}

/**
 * Revoke an ASI role assignment.
 * Callable: revokeAsiRoleAssignment
 * @param {{
 *   uid: string,
 *   scopeType: string,
 *   scopeId: string,
 *   institutionId?: string,
 * }} payload
 */
export async function revokeAsiRoleAssignment(payload) {
  const callable = getFunctions().httpsCallable("revokeAsiRoleAssignment");
  const result = await callable(payload);
  return result.data;
}

/**
 * List ASI role assignments for an institution (optional scope filter).
 * Callable: listAsiRoleAssignments
 * @param {{
 *   institutionId: string,
 *   scopeType?: string,
 *   scopeId?: string,
 * }} payload
 * @returns {Promise<{ assignments: Array<object> }>}
 */
export async function listAsiRoleAssignments(payload) {
  const callable = getFunctions().httpsCallable("listAsiRoleAssignments");
  const result = await callable(payload);
  return result.data;
}

/**
 * Org-wide ASI readiness snapshot (ASI Readiness Lead / canViewReadiness).
 * Callable: getAsiOrgReadiness
 * @param {{ institutionId: string }} payload
 */
export async function getAsiOrgReadiness(payload) {
  const callable = getFunctions().httpsCallable("getAsiOrgReadiness");
  const result = await callable(payload);
  return result.data;
}
